import { Menu, Transition } from '@headlessui/react'
import {
  MenuIcon,
  XIcon
} from '@heroicons/react/outline'
import React, { Fragment } from 'react'
import {
  InformationCircleIcon,
  HeartIcon,
  UserIcon,
  AcademicCapIcon,
  SparklesIcon,
} from '@heroicons/react/outline'
import { Link } from 'react-router-dom'
import { User } from '../../lib/user'

type Icon = (props: React.SVGProps<SVGSVGElement>) => JSX.Element

type Props = {
  user: User
  setIsSupportModalOpen: (value: boolean) => void
  setIsProfileModalOpen: (value: boolean) => void
  setIsInfoModalOpen: (value: boolean) => void
}

export const BaseMenu = ({
  user,
  setIsSupportModalOpen,
  setIsProfileModalOpen,
  setIsInfoModalOpen,
}: Props) => {

  const item = (title: string, description: string, href: string, pplus=false) => {
    const needsPplus = pplus && !user.hasPolygonlePlus()
    return <div className="px-1 py-1 relative overflow-hidden">
      <Menu.Item>
                {({ active, close }) => (
                  <>
                  {needsPplus && <div className="absolute left-0 top-0 h-12 w-12">
                      <div
                        className="absolute transform -rotate-[45deg] bg-indigo-600 text-center text-sm text-white font-semibold py-2 left-[-45px] top-[12px] w-[170px]">
                        Polygonle+
                      </div>
                  </div>
                  }
                  <Link
                    to={href}
                    onClick={close}
                    className={`hover:bg-indigo-400 hover:text-white
                      ${ needsPplus ? 'text-gray-500' : 'text-gray-900 dark:text-white'} group flex flex-col w-full items-center rounded-md px-2 py-2 title text-lg`}>
                    <div className="font-bold">{title}</div>
                    <div className="text-sm text-center">{description}</div>
                  </Link>
                </>
                )}
              </Menu.Item>
            </div>
  }

  const iconItem = (IconTag: Icon, description: string, label: string, onClick: (value: boolean) => void) => {
    return <div className="px-1 py-1">
      <Menu.Item>
                {({ active }) => (
                  <button
                    className={`${
                      active ? 'bg-indigo-400 text-white' : 'text-gray-900'
                    } dark:text-white group flex w-full items-center rounded-md px-4 py-2 title text-lg`}
                    onClick={() => onClick(true)} >
                    <IconTag 
                      id={`menu-btn-${label}`}
                      className="h-6 w-6 mr-2 scale-up cursor-pointer dark:stroke-white"
                      aria-label={label}
                      role="button"
                      tabIndex={0}
                    />
                    <div className="text-sm">{description}</div>
                  </button>
                )}
              </Menu.Item>
            </div>
  }
  const iconLink = (IconTag: Icon, description: string, label: string, href: string) => {
    return <div className="px-1 py-1">
      <Menu.Item>
                {({ active }) => (
                  <Link
                    to={href}
                    className={`${
                      active ? 'bg-indigo-400 text-white' : 'text-gray-900'
                    } dark:text-white group flex w-full items-center rounded-md px-4 py-2 title text-lg`} >
                    <IconTag 
                      id={`menu-btn-${label}`}
                      className="h-6 w-6 mr-2 scale-up cursor-pointer dark:stroke-white"
                      aria-label={label}
                      role="button"
                      tabIndex={0}
                    />
                    <div className="text-sm">{description}</div>
                  </Link>
                )}
              </Menu.Item>
            </div>
  }
  return ( <div className="text-right">
      <Menu as="div" className="relative inline-block text-left">
        <div>
          <Menu.Button>
          <MenuIcon
            className="h-6 w-6 mr-2 scale-up cursor-pointer dark:stroke-white"
            id="nav-btn-info"
            aria-label="info"
            role="button"
            tabIndex={0}
          />
          </Menu.Button>
        </div>
        <Transition
          as={Fragment}
          enter="transition ease-in-out duration-300"
          enterFrom="transform -translate-x-80"
          enterTo="transform translate-x-0"
          leave="transition ease-out-in duration-300"
          leaveFrom="transform translate-x-0"
          leaveTo="transform -translate-x-80"
        >
          <Menu.Items className="absolute -left-3 -mt-10 w-56 origin-top-right divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800 shadow-lg ring-1 ring-black ring-opacity-5 dark:ring-opacity-55555cus:outline-none z-20">
            <div className="flex items-center justify-between pl-4 py-4">
              <span className="pt-1 text-2xl font-bold uppercase title dark:text-white"> Menu </span>
              <Menu.Item>
                {({ close }) => (
                  <XIcon id="close-menu"
                    className="p-2 h-10 w-10 mr-2 scale-up cursor-pointer dark:stroke-white"
                    aria-label="close-menu"
                    role="button"
                    tabIndex={0}
                    onClick={close}
                  />
                )}
              </Menu.Item>
            </div>

            {item('Daily', 'The original classic', '/')}
            {item('Unlimited', 'Endless puzzles', '/?unlimited')}
            {item('Mini', 'A bite-sized puzzle', '/mini')}
            {item('Archive', 'Revisit old classics', '?archive')}
            {item('Custom', 'Create your own puzzle', '?custom')}
            {iconItem(InformationCircleIcon,
              'How to play',
              'info',
              setIsInfoModalOpen)}
            {iconLink(AcademicCapIcon,
              'Tips & Tricks',
              'tips',
              '/tips'
             )}
            {iconItem(UserIcon,
              user.isAuthed() ? 'Profile & Signout': 'Sign in',
              user.isAuthed() ? 'profile': 'signin',
              setIsProfileModalOpen)}
            {iconLink(SparklesIcon,
              'Achievements',
              'achievements',
              '/achievements'
             )}
            {iconItem(HeartIcon,
              'Community',
              'community',
              setIsSupportModalOpen)}
            <div className="px-1 py-1 relative overflow-hidden">
              <Menu.Item>
                {({ active }) => (
                  <a href={"mailto:grant.warman+polygonle@gmail.com"}
                    className={`hover:bg-indigo-400 hover:text-white
                      group flex flex-col w-full items-center rounded-md px-2 py-2 title text-md dark:text-white`}>
                    <div className="font-bold">Contact Us</div>
                  </a>
                )}
              </Menu.Item>
            </div>
            <div className="glow bg-white dark:bg-gray-800">
            {!user.hasPolygonlePlus() && item('Join Polygonle+', 'Unlock exclusive features', '/?pplus')}
            </div>

          </Menu.Items>
        </Transition>
      </Menu>
    </div>
  )

}
